import { useEffect } from "react";
import ConwayHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Conway?", a: "We offer emergency processing as fast as 24 hours. Standard expedited service takes 7–10 business days. We serve all of Faulkner County with the same speed options." },
  { q: "Where is the nearest passport acceptance facility in Conway?", a: "The Conway Post Office and the Conway City Hall are both passport acceptance facilities. We help coordinate your appointment scheduling to minimize wait time." },
  { q: "Can UCA students get expedited passports through your service?", a: "Absolutely. We regularly assist UCA and Hendrix College students with passport applications for study abroad programs, international internships, and academic travel. We offer group processing for study abroad cohorts." },
  { q: "How long does it take for a passport renewal from Conway?", a: "With our standard expedited service, passport renewals typically arrive in 7–10 business days from when we receive your documents. Emergency renewals are available in 24–72 hours." },
  { q: "Can I apply for a child passport in Conway?", a: "Yes. Children under 16 need both parents present at an acceptance facility or a notarized DS-3053 form. We guide Conway families through every step of the child passport application." },
  { q: "Does Conway have a passport office?", a: "Conway does not have a dedicated passport office, but has several acceptance facilities including the Post Office and City Hall. We handle all the logistics so you only visit once." },
  { q: "How much does expedited passport service cost in Conway?", a: "Government fees are $130 for an adult passport book. Our service fees vary by speed. We typically charge 30–100% less than FedEx and Staples, with a best price guarantee." },
  { q: "Can I renew my passport by mail from Conway?", a: "If your passport was issued within the last 15 years and you were 16 or older at the time, you can renew by mail. Our mail-in renewal service handles all the logistics." },
];

export default function ConwayPage() {
  useEffect(() => {
    document.title = "Emergency Passport Conway, AR — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/conway/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Conway, AR — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving UCA students, Hendrix College faculty, and Faulkner County families.");
    setMeta("geo.placename", "Conway, Arkansas");
    setMeta("geo.region", "US-AR");
    setMeta("geo.position", "35.0887;-92.4421");
    setMeta("ICBM", "35.0887, -92.4421");
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Arkansas", item: "https://fastpassportcenter.com/arkansas/" },
        { "@type": "ListItem", position: 3, name: "Conway Passport Services", item: pageUrl },
      ]},
      { "@type": "LocalBusiness", name: "Fast Passport Center", url: pageUrl, telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Conway", containedInPlace: { "@type": "State", name: "Arkansas" } },
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json"; script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => { document.getElementById("structured-data")?.remove(); document.querySelector('link[rel="canonical"]')?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <ConwayHeroSection />
      <nav className="sticky top-[70px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 overflow-x-auto py-3 no-scrollbar">
          {["Services","Processing","Local Area","Timing","How It Works","FAQ"].map((label) => (
            <a key={label} href={`#${label.toLowerCase().replace(/ /g, "-")}`} className="text-sm font-medium text-[#1A365D] hover:text-[#F6C344] whitespace-nowrap px-3 py-1 rounded-md hover:bg-[#EEF2F8] transition-all">{label}</a>
          ))}
          <a href="https://fastpassportcenter.com/checkout/" className="ml-auto text-sm font-bold bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] px-4 py-1.5 rounded-md whitespace-nowrap transition-all">Get Started</a>
        </div>
      </nav>

      <section id="processing" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Conway?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { speed: "Same-Day Emergency", time: "24–72 Hours", price: "From $599", best: true, desc: "For students with urgent study abroad departures and families with imminent trips." },
              { speed: "Expedited Rush", time: "3–5 Business Days", price: "From $349", best: false, desc: "Ideal for Conway residents with travel within 2 weeks. Full tracking included." },
              { speed: "Standard Expedited", time: "7–10 Business Days", price: "From $199", best: false, desc: "Great for planned travel in 3–6 weeks. Best value with reliable processing." },
            ].map((opt) => (
              <div key={opt.speed} className={`relative rounded-lg border p-8 flex flex-col ${opt.best ? "border-[#F6C344] bg-[#FEFDF8]" : "border-gray-200 bg-white"}`}>
                {opt.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F6C344] text-[#1A365D] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">Most Popular</span>}
                <h3 className="font-bold text-[#1A365D] text-xl mb-2">{opt.speed}</h3>
                <p className="text-[#F6C344] font-bold text-2xl mb-1">{opt.time}</p>
                <p className="text-gray-500 text-sm mb-4">{opt.desc}</p>
                <div className="mt-auto">
                  <p className="text-[#1A365D] font-bold text-lg mb-4">{opt.price}</p>
                  <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0F2440] text-white font-semibold text-sm py-3 rounded-md transition-all whitespace-nowrap">Select This Speed</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Offer</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Conway &amp; Faulkner County</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: "ri-flashlight-line", title: "Emergency Passport", desc: "24–72 hour processing for urgent travel. Available for UCA students with late study abroad confirmations." },
              { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for expired passports. Mail-in renewal available for eligible applicants." },
              { icon: "ri-passport-line", title: "New Passport", desc: "First-time application guidance with scheduling at Conway acceptance facilities." },
              { icon: "ri-alert-line", title: "Lost/Stolen Passport", desc: "DS-64 filing and replacement. Emergency service available within 24–72 hours." },
              { icon: "ri-parent-line", title: "Child Passport", desc: "Under-16 passport services with parental consent and document preparation assistance." },
              { icon: "ri-graduation-cap-line", title: "Student Study Abroad", desc: "Group processing for UCA and Hendrix College study abroad cohorts. We coordinate everything." },
              { icon: "ri-stack-line", title: "Second Passport", desc: "For frequent travelers needing two valid passports simultaneously." },
              { icon: "ri-tools-line", title: "Damaged Passport", desc: "Replacement for water-damaged, torn, or otherwise compromised passports." },
              { icon: "ri-edit-line", title: "Name Change", desc: "Marriage, divorce, or legal name change updates with court document guidance." },
            ].map((s) => (
              <div key={s.title} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className={`${s.icon} text-[#1A365D] text-xl`}></i></div>
                <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="local-area" className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area Coverage</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Across Conway and Faulkner County</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p>
                <strong className="text-[#1A365D]">Conway is home to three universities:</strong> the University of Central Arkansas (UCA) with 12,000+ students, Hendrix College, and Central Baptist College. Each year, hundreds of students receive study abroad acceptances to programs in Europe, Latin America, and Asia on very short notice. We specialize in rapid passport processing for students who have just 4–6 weeks before their semester abroad begins, a timeline that makes standard government processing impossible without our help.
              </p>
              <p>
                <strong className="text-[#1A365D]">Hendrix College</strong> has a robust international programs office and sends faculty-led groups to Oxford, Florence, and various field research destinations annually. We have experience coordinating group passport processing for entire academic cohorts, ensuring every student and faculty member has their documents in order before departure.
              </p>
              <p>
                Beyond the university community, Conway serves as a bedroom community for Little Rock's professional class, with many <strong className="text-[#1A365D]">Greenbrier, Vilonia, Mayflower, and Bigelow</strong> residents also commuting through the area. We serve all of these surrounding communities with the same rapid turnaround. Conway families planning their first international cruise or Disney trip to Europe frequently use our expedited processing.
              </p>
              <p>
                <strong className="text-[#1A365D]">Bill and Hillary Clinton National Airport (LIT)</strong> is just 30 minutes south via I-40, offering direct service to major hubs for all international connections. We ensure every Conway traveler has their passport ready well before their departure date.
              </p>
            </div>
            <div className="space-y-4">
              <div className="bg-[#F7F8FA] rounded-lg p-6">
                <h4 className="font-bold text-[#1A365D] mb-4">Areas We Serve</h4>
                <div className="grid grid-cols-2 gap-3">
                  {["Conway","Greenbrier","Vilonia","Mayflower","Bigelow","Morrilton","Perry","Perryville","Maumelle","Winrock","Quitman","Damascus"].map((area) => (
                    <div key={area} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-map-pin-line text-[#F6C344]"></i>{area}</div>
                  ))}
                </div>
              </div>
              <div className="bg-[#F7F8FA] rounded-lg p-6">
                <h4 className="font-bold text-[#1A365D] mb-4">Nearby Airports</h4>
                <div className="space-y-2">
                  {[
                    { name: "Clinton National Airport (LIT)", dist: "30 min via I-40" },
                    { name: "Memphis International (MEM)", dist: "150 miles east" },
                    { name: "Dallas/Fort Worth (DFW)", dist: "320 miles south" },
                  ].map((a) => (
                    <div key={a.name} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{a.name}</span><span className="text-gray-400">{a.dist}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="timing" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Best Times to Apply from Conway</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { season: "Spring (Mar–May)", tag: "Study Abroad Peak", desc: "Maymester and summer study abroad programs generate high passport demand. Apply immediately upon program acceptance." },
              { season: "Summer (Jun–Aug)", tag: "Family Season", desc: "Conway families planning international vacations. Fall semester study abroad programs start in August — apply in June." },
              { season: "Fall/Winter (Sep–Feb)", tag: "Best Processing", desc: "Fastest government processing times. Spring semester study abroad programs require applications by November–December." },
            ].map((t) => (
              <div key={t.season} className="bg-white rounded-lg p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-[#1A365D] text-lg">{t.season}</h3>
                  <span className="text-xs font-semibold bg-[#EEF2F8] text-[#1A365D] px-2 py-1 rounded-full whitespace-nowrap">{t.tag}</span>
                </div>
                <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Conway Travelers Are Going</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { dest: "Italy & Spain", icon: "ri-building-2-line", desc: "UCA and Hendrix study abroad programs in Rome, Florence, and Madrid. Peak: May–August." },
              { dest: "Caribbean Cruises", icon: "ri-anchor-line", desc: "Cruise departures from New Orleans (5 hrs south). Family vacations year-round." },
              { dest: "UK & Ireland", icon: "ri-building-3-line", desc: "Academic exchanges and alumni travel tours. Summer and winter break." },
              { dest: "Mexico & Central America", icon: "ri-map-line", desc: "Church mission trips and volunteer programs. Spring break and summer peaks." },
            ].map((d) => (
              <div key={d.dest} className="bg-[#F7F8FA] rounded-lg p-6 text-center hover:bg-[#EEF2F8] transition-all">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white mx-auto mb-4"><i className={`${d.icon} text-[#1A365D] text-2xl`}></i></div>
                <h3 className="font-bold text-[#1A365D] mb-2">{d.dest}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1A365D] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Traveler Reviews</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">What Conway Residents Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "Emily K.", location: "UCA Student, Conway", text: "I got accepted to a study abroad program in Florence with only 5 weeks notice. Fast Passport Center got my first-ever passport in 4 days. I made it to Italy without any stress." },
              { name: "Dr. Robert H.", location: "Hendrix College, Conway", text: "I lead a faculty-led group to Oxford every year. Fast Passport Center coordinates all 12 student passports for me. Flawless every time. The best service for academic travel." },
              { name: "The Wilson Family", location: "Greenbrier, AR", text: "We surprised the kids with a Disney cruise but realized none of us had valid passports. Fast Passport Center got all 5 family passports in 6 days. Best vacation ever!" },
            ].map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur rounded-lg p-6 border border-white/10">
                <div className="flex items-center gap-1 mb-4">{[1,2,3,4,5].map((s) => <i key={s} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
                <p className="text-white/90 text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#F6C344] flex items-center justify-center text-[#1A365D] font-bold text-sm">{t.name[0]}</div>
                  <div><p className="text-white font-semibold text-sm">{t.name}</p><p className="text-white/60 text-xs">{t.location}</p></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Conway</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: "Conway Post Office", address: "800 Harkrider St, Conway, AR 72032", phone: "(501) 327-8421", type: "Post Office", note: "Appointment required. Photo services available on-site." },
              { name: "Conway City Hall", address: "1201 Oak St, Conway, AR 72032", phone: "(501) 450-6100", type: "City Hall", note: "Serves Faulkner County residents. Call for passport acceptance hours." },
            ].map((f) => (
              <div key={f.name} className="bg-[#F7F8FA] rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-government-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">{f.type}</span></div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600"><i className="ri-map-pin-line text-[#F6C344]"></i>{f.address}</div>
                  <div className="flex items-center gap-2 text-gray-600"><i className="ri-phone-line text-[#F6C344]"></i>{f.phone}</div>
                  <div className="flex items-center gap-2 text-gray-500"><i className="ri-information-line text-[#F6C344]"></i>{f.note}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Other Options</h2>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-4 bg-[#1A365D] text-white text-sm font-semibold py-4 px-6">
              <div>Feature</div><div className="text-center">Fast Passport Center</div><div className="text-center">FedEx / Staples</div><div className="text-center">DIY at Post Office</div>
            </div>
            {[["Speed","24 hrs – 10 days","2–6 weeks","8–11 weeks"],["Price","30–100% lower","Expensive markup","Lowest base cost"],["Personal Support","Dedicated agent","Limited / None","None"],["Document Review","Expert review included","Extra fee","Self-check"],["Real-Time Tracking","Yes","Basic","No"],["State Dept. Courier","Registered","Reseller","N/A"],["Best Price Guarantee","Yes","No","N/A"],["A+ BBB Rating","Yes","Varies","N/A"],["Money-Back Guarantee","Yes","Varies","No"]].map((row, i) => (
              <div key={i} className={`grid grid-cols-4 py-4 px-6 text-sm ${i % 2 === 0 ? "bg-[#F7F8FA]" : "bg-white"}`}>
                <div className="text-[#1A365D] font-semibold">{row[0]}</div><div className="text-center text-green-700 font-medium">{row[1]}</div><div className="text-center text-gray-500">{row[2]}</div><div className="text-center text-gray-500">{row[3]}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Don't Let These Errors Delay Your Study Abroad</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Wrong Photo Size", desc: "Must be 2x2 inches with white background. Many campus photo stations use non-compliant formats. We provide exact specifications." },
              { title: "Missing Citizenship Proof", desc: "First-time applicants need an original birth certificate. Photocopies are not accepted. We guide you on proper documentation." },
              { title: "Incomplete DS-11 Forms", desc: "Missing signatures are the #1 reason for delays. We review every form before submission to catch errors early." },
              { title: "Wrong Payment Amount", desc: "Government fees change. We confirm current amounts so your check or money order is accurate before submission." },
              { title: "Late Application for Study Abroad", desc: "Study abroad programs often provide acceptance with only 4–6 weeks notice. Contact us immediately to start emergency processing." },
              { title: "Missing Parental Consent", desc: "Students under 18 with separated parents must provide specific consent documentation. We guide families through the process." },
            ].map((m) => (
              <div key={m.title} className="bg-[#F7F8FA] rounded-lg p-6 border-l-4 border-[#F6C344]">
                <h3 className="font-bold text-[#1A365D] mb-2">{m.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Simple Process</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works in 4 Steps</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: "01", title: "Start Online", desc: "Choose your processing speed and complete our secure online application. Takes 5 minutes." },
              { step: "02", title: "Document Prep", desc: "We review your documents and coordinate your acceptance facility visit in Conway." },
              { step: "03", title: "Courier Handoff", desc: "Our registered State Department courier picks up your sealed envelope and delivers it directly." },
              { step: "04", title: "Passport Delivered", desc: "Your passport arrives at your Conway address via secure overnight shipping." },
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-lg p-6 text-center border border-gray-200">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4"><span className="text-[#1A365D] font-bold text-xl">{s.step}</span></div>
                <h3 className="font-bold text-[#1A365D] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <details key={i} className="bg-[#F7F8FA] rounded-lg border border-gray-200 group">
                <summary className="flex items-center justify-between p-5 cursor-pointer text-[#1A365D] font-semibold text-base hover:text-[#F6C344] transition-colors">
                  {faq.q}<i className="ri-add-line text-[#F6C344] group-open:rotate-45 transition-transform"></i>
                </summary>
                <p className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F7F8FA] py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Links for Conway Travelers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "U.S. State Department", desc: "Official passport requirements and forms.", link: "https://travel.state.gov/content/travel/en/passports.html" },
              { title: "UCA Study Abroad", desc: "University of Central Arkansas international programs office.", link: "https://uca.edu/international/" },
              { title: "Hendrix College Global Programs", desc: "Hendrix College international and study abroad programs.", link: "https://www.hendrix.edu/global/" },
              { title: "TSA PreCheck", desc: "Expedited airport security at Clinton National Airport.", link: "https://www.tsa.gov/precheck" },
              { title: "Smart Traveler Enrollment", desc: "Register your trip with the U.S. Embassy.", link: "https://step.state.gov/" },
              { title: "CDC Travel Health", desc: "Health advisories and vaccination requirements.", link: "https://wwwnc.cdc.gov/travel" },
            ].map((r) => (
              <a key={r.title} href={r.link} target="_blank" rel="noopener noreferrer nofollow" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
                <h3 className="font-bold text-[#1A365D] mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
                <p className="text-gray-500 text-sm mb-3">{r.desc}</p>
                <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-medium group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <SEOCrossLinks currentRoute="/cities/conway" state="AR" />

      <section className="bg-[#1A365D] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Get Your Passport in Conway?</h2>
          <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            UCA students, Hendrix faculty, or Conway families — we get your passport done fast, no matter your timeline or destination.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
              Start My Passport <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:8772530084" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i>877-253-0084
            </a>
          </div>
          <p className="text-white/50 text-sm mt-6">Free consultation. No obligation. A+ BBB rated.</p>
        </div>
      </section>
    </div>
  );
}